// Digital Signage → Vista previa interna. Envuelve el reproductor compartido
// (player.tsx) con selector de local + tema (preset) + pantalla completa, usando
// el catálogo real del local (RPC pos.sig_catalogo). Para el reproductor real por
// token, ver SignageTV.tsx.
import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Maximize2, Minimize2 } from 'lucide-react';
import { locationsList, posRpc } from '@/features/pos/shared';
import type { LocationOpt } from '@/features/pos/shared';
import { SignagePlayer, TRANSICIONES, playerCss } from './player';
import type { Categoria, GadgetsCfg, TemaTokens, TransKind } from './player';
import { CAMPANAS } from './CampaignOverlay';

const PRESETS: Record<string, TemaTokens> = {
  calido: { paper: '#efe7da', paper2: '#e7ddcc', ink: '#191310', soft: '#6f655a', accent: '#c6303a', sage: '#4f7a52', oxido: '#b0552a', topo: '#8a8175' },
  frio: { paper: '#eceae3', paper2: '#e3e1d8', ink: '#15181b', soft: '#6b6f76', accent: '#2b3a67', sage: '#5b7a5b', oxido: '#a6564b', topo: '#8a8175' },
  oscuro: { paper: '#0e0f13', paper2: '#171a22', ink: '#f2eee4', soft: '#9aa0aa', accent: '#c9a86a', sage: '#6cc08c', oxido: '#e0a24a', topo: '#79736a' },
};

interface CatData { local: string | null; empresa: string | null; categorias: Categoria[] }

export function SignagePreview() {
  const boxRef = useRef<HTMLDivElement>(null);
  const [locationId, setLocationId] = useState('');
  const [preset, setPreset] = useState<'calido' | 'frio' | 'oscuro'>('calido');
  const [campana, setCampana] = useState('');
  const [intensidad, setIntensidad] = useState(2);
  const [orient, setOrient] = useState<'h' | 'v'>('h');
  const [trans, setTrans] = useState<TransKind>('fundido');
  const [videoUrl, setVideoUrl] = useState('');
  const [gadgetsDemo, setGadgetsDemo] = useState(false);
  const [full, setFull] = useState(false);

  const { data: locations = [] } = useQuery<LocationOpt[]>({ queryKey: ['pos-locations'], queryFn: locationsList });
  useEffect(() => { if (!locationId && locations.length) setLocationId(locations[0].id); }, [locationId, locations]);

  const { data } = useQuery<CatData>({
    queryKey: ['sig-catalogo', locationId],
    queryFn: () => posRpc<CatData>('sig_catalogo', { _location: locationId }),
    enabled: !!locationId, staleTime: 0, refetchInterval: 60000,
  });

  const playerData = useMemo(() => ({
    empresa: data?.empresa, local: data?.local,
    tema: { preset, tokens: PRESETS[preset] },
    escenas: [
      ...(videoUrl ? [{ tipo: 'video', dur: 20, url: videoUrl, titulo: 'Video', trans }] : []),
      { tipo: 'catalogo', dur: 9, trans }, { tipo: 'carta', dur: 16, titulo: 'La carta', trans },
    ],
    categorias: data?.categorias ?? [],
    // Gadgets de ejemplo: muestra clima/UV en el header (con las coordenadas del
    // local si las tiene; Guayaquil de respaldo) + chips de demostración.
    ...(gadgetsDemo ? {
      lat: -2.19, lon: -79.89,
      gadgets: {
        activo: true, clima: true, uv: true, fecha: true,
        items: [
          { tipo: 'saludo', pos: 'sup-centro' },
          { tipo: 'local', pos: 'inf-izq', texto: 'Abierto hasta las 22:00' },
          { tipo: 'cuenta', pos: 'inf-der', titulo: 'Fin de la promo', fecha: new Date(Date.now() + 26 * 3600e3).toISOString() },
        ],
      } as GadgetsCfg,
    } : {}),
  }), [data, preset, trans, videoUrl, gadgetsDemo]);

  useEffect(() => {
    const onFs = () => setFull(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', onFs);
    return () => document.removeEventListener('fullscreenchange', onFs);
  }, []);
  const toggleFull = () => { if (document.fullscreenElement) void document.exitFullscreen(); else void boxRef.current?.requestFullscreen(); };

  return (
    <div className="p-4 space-y-3">
      <style>{playerCss}</style>
      <div className="flex items-center gap-3 flex-wrap">
        <h1 className="text-lg font-semibold">Digital Signage — <span className="text-primary">Vista previa</span></h1>
        <select className="h-9 rounded-md border bg-background px-2 text-sm" value={locationId} onChange={(e) => setLocationId(e.target.value)}>
          {locations.map((l) => <option key={l.id} value={l.id}>{l.nombre}</option>)}
        </select>
        <div className="inline-flex rounded-full border overflow-hidden text-sm">
          {([['calido', 'Cálido'], ['frio', 'Frío'], ['oscuro', 'Oscuro']] as const).map(([v, l]) => (
            <button key={v} className={`px-3 py-1.5 ${preset === v ? 'bg-foreground text-background font-medium' : 'text-muted-foreground'}`}
                    onClick={() => setPreset(v)}>{l}</button>
          ))}
        </div>
        <div className="inline-flex rounded-full border overflow-hidden text-sm">
          {([['h', '16:9'], ['v', '9:16']] as const).map(([v, l]) => (
            <button key={v} className={`px-3 py-1.5 ${orient === v ? 'bg-foreground text-background font-medium' : 'text-muted-foreground'}`}
                    onClick={() => setOrient(v)}>{l}</button>
          ))}
        </div>
        <select className="h-9 rounded-md border bg-background px-2 text-sm" value={trans} onChange={(e) => setTrans(e.target.value as TransKind)} title="Transición entre escenas">
          {TRANSICIONES.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
        </select>
        <input className="h-9 rounded-md border bg-background px-2 text-sm w-64" placeholder="URL de video (prueba)" value={videoUrl} onChange={(e) => setVideoUrl(e.target.value)} title="Pega un MP4/YouTube/Vimeo/HLS .m3u8 para verlo como escena" />
        <label className="inline-flex items-center gap-1.5 text-sm cursor-pointer" title="Muestra clima/UV en el header y chips de ejemplo (saludo, texto del local, cuenta regresiva)">
          <input type="checkbox" className="accent-primary" checked={gadgetsDemo} onChange={(e) => setGadgetsDemo(e.target.checked)} /> Gadgets de ejemplo
        </label>
        <button onClick={toggleFull} className="ml-auto inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-sm hover:bg-muted">
          {full ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />} {full ? 'Salir' : 'Pantalla completa'}
        </button>
      </div>

      <div className="flex items-center gap-3 flex-wrap text-sm">
        <span className="text-muted-foreground">Campaña estacional:</span>
        <select className="h-9 rounded-md border bg-background px-2" value={campana} onChange={(e) => setCampana(e.target.value)}>
          {CAMPANAS.map((c) => <option key={c.v} value={c.v}>{c.l}</option>)}
        </select>
        {campana && (
          <div className="inline-flex items-center gap-1.5">
            <span className="text-muted-foreground">Intensidad</span>
            {[1, 2, 3].map((n) => (
              <button key={n} className={`w-7 h-7 rounded-md border ${intensidad === n ? 'bg-foreground text-background font-medium' : 'text-muted-foreground'}`} onClick={() => setIntensidad(n)}>{n}</button>
            ))}
          </div>
        )}
      </div>

      <div ref={boxRef} className={`rounded-xl overflow-hidden shadow-2xl bg-black ${orient === 'v' ? 'aspect-[9/16] h-[72vh] mx-auto' : 'w-full aspect-video'}`}>
        <SignagePlayer data={playerData} campana={campana ? { efecto: campana, intensidad, saludo: CAMPANAS.find((c) => c.v === campana)?.saludo } : null} />
      </div>

      <p className="text-xs text-muted-foreground">
        Vista previa con datos reales del local (foto, precio y stock del ERP). Rota entre <strong>Catálogo hero</strong> y
        <strong> Menú a la carta</strong>. Este es el mismo reproductor que corre por token en las pantallas
        (<strong>Signage → Pantallas</strong>); aquí es solo para validar el diseño.
      </p>
    </div>
  );
}
